import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { type FantasyLineupItem } from '@app/client/common/structs';
import { type WishlistSummaryResponse } from '@app/client/resources/wishlists/types';
import { type LineupItemProps } from '@app/pages/FantasyWishlist/components/FantasySidebar/types';

type FantasyLineupStateType = {
    lineupItems: FantasyLineupItem[];
    lineupListItems: LineupItemProps[];
    currentUserLineupSummary?: WishlistSummaryResponse;
    newItemAdded?: string | null;
};

interface FantasyLineupState extends FantasyLineupStateType {
    setLineupItems: (lineupItems: FantasyLineupItem[]) => void;
    setLineupListItems: (lineupListItems: LineupItemProps[]) => void;
    updateItemFloorPrice: (update: { slug: string; floorPrice: string }) => void;
    addLineupItem: (item: FantasyLineupItem) => void;
    updateItemQuantity: (item: { slug: string; increment: number }) => void;
    removeLineupItem: (item: { slug: string }) => void;
    clearLineupItems: () => void;
    setCurrentUserLineupSummary: (lineupSummary: WishlistSummaryResponse) => void;
    setNewItemAdded: (newItemAdded: string | null) => void;
}

const initialState: FantasyLineupStateType = {
    lineupItems: [],
    lineupListItems: [],
    currentUserLineupSummary: undefined,
};

export const useFantasyLineupStore = create<FantasyLineupState>()(
    devtools(
        (set) => ({
            ...initialState,
            setLineupListItems: (lineupListItems) => set({ lineupListItems }),
            updateItemFloorPrice: (update) =>
                set((state) => {
                    return {
                        lineupItems: state.lineupItems.map((item) => {
                            if (item.slug === update.slug) {
                                return { ...item, floorPrice: update.floorPrice };
                            }
                            return item;
                        }),
                    };
                }),
            updateItemQuantity: (itemData) => {
                set((state) => {
                    return {
                        lineupItems: state.lineupItems.map((item) => {
                            if (item.slug === itemData.slug)
                                return { ...item, quantity: (item.quantity || 1) + itemData.increment };
                            return item;
                        }),
                    };
                });
            },
            addLineupItem: (lineupItem) =>
                set((state) => {
                    if (state.lineupItems.some((item) => item.slug === lineupItem.slug)) {
                        return {
                            lineupItems: state.lineupItems.map((item) => {
                                if (item.slug === lineupItem.slug)
                                    return { ...item, quantity: lineupItem.quantity, confirmed: lineupItem.confirmed };
                                return item;
                            }),
                        };
                    }
                    return { lineupItems: [lineupItem, ...state.lineupItems] };
                }),
            removeLineupItem: (lineupItem) =>
                set((state) => ({
                    lineupItems: state.lineupItems?.filter((item) => item.slug !== lineupItem.slug),
                })),
            setLineupItems: (newLineupItems) =>
                set((state) => {
                    if (!state.lineupItems.length) {
                        return {
                            lineupItems: newLineupItems,
                        };
                    }

                    const newItems = newLineupItems.filter(
                        (newItem) => !state.lineupItems.some((currItem) => currItem.slug === newItem.slug),
                    );

                    const updatedItems = state.lineupItems.map((item) => {
                        const itemUpdated = newLineupItems.find((newItem) => newItem.slug === item.slug);
                        if (itemUpdated) return { ...item, ...itemUpdated };
                        return item;
                    });

                    return {
                        lineupItems: [...newItems, ...updatedItems],
                    };
                }),
            clearLineupItems: () => set({ ...initialState }),
            setCurrentUserLineupSummary: (userLineupCollectionSummary) =>
                set({ currentUserLineupSummary: userLineupCollectionSummary }),
            setNewItemAdded: (value) => set({ newItemAdded: value }),
        }),
        { name: 'fantasy-lineup-items-storage' },
    ),
);
